import React, {useState} from "react";
import {Button, ButtonGroup, Checkbox, CheckboxGroup,Radio, RadioGroup, Select, SingleValue, TextField, Modal, Text} from "@nike/eds";
import {edsSpace16, edsSpace32} from "@nike/eds/dist/tokens";
import {useParams} from "react-router-dom";
import QuantityAdjustmentDrsModal from "./QuantityAdjustmentDrsModal";
import { AlertCircle } from "@nike/nike-design-system-icons";

enum Type {
    NEGADJ = "NEGADJ"
}

const reasonOptions = [
    {value: "Adjust All - AA", label: "Adjust All - AA"},
    {value: "Incorrect Status - IS", label: "Incorrect Status - IS"},
    {value: "Clean Up - KVC", label: "Clean Up - KVC"}
];

function QuantityAdjustmentDrsForm({inventoryService, onClose, host, location}) {
    const params = useParams();

    const [formState, setFormState] = useState({
        adjustmentType: Type.NEGADJ,
        quantity: 0,
        transportHolderNumber: "",
        billOfLading: "",
        reason: "",
        comment: "",
        quantityHasErrors: false,
        quantityErrorMessage: "",
        transportHolderNumberHasErrors: false,
        transportHolderNumberErrorMessage: "",
        billOfLadingHasErrors: false,
        billOfLadingErrorMessage: "",
        reasonHasErrors: false,
        reasonErrorMessage: "",
        clearGoodsIfNeeded: true
    });

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [drsModalOpen, setDrsModalOpen] = useState(false);

    // Add state to store the values for the error modal
    const [errorModalData, setErrorModalData] = useState<{ transportHolderNumber: string; billOfLading: string } | null>(null);

    const handleInputChange = (key: string, value: any) => {
        setFormState((prevState) => ({
            ...prevState,
            [key]: value
        }));
    };

    const handleQuantityFocus = () => {
        if (formState.quantity === 0) {
            setFormState((prevState) => ({
                ...prevState,
                quantity: NaN
            }));
        }
    };

    const validate = () => {
        const {quantity, transportHolderNumber, billOfLading, reason} = formState;

        let quantityErrorMessage = "";
        let quantityHasErrors = false;
        let transportHolderNumberErrorMessage = "";
        let transportHolderNumberHasErrors = false;
        let billOfLadingErrorMessage = "";
        let billOfLadingHasErrors = false;
        let reasonErrorMessage = "";
        let reasonHasErrors = false;

        // Validate quantity (same as ELC)
        if (quantity === 0 || Number.isNaN(quantity)) {
            quantityErrorMessage = "No quantity added";
            quantityHasErrors = true;
        } else if (quantity < 0) {
            quantityErrorMessage = "Quantity value can not be negative";
            quantityHasErrors = true;
        } else if (quantity % 1 !== 0) {
            quantityErrorMessage = "Quantity value can not be a decimal";
            quantityHasErrors = true;
        }

        // Validate transportHolderNumber
        if (!transportHolderNumber.trim()) {
            transportHolderNumberErrorMessage = "No transportholder number added";
            transportHolderNumberHasErrors = true;
        }

        // Validate billOfLading
        if (!billOfLading.trim()) {
            billOfLadingErrorMessage = "No bill of lading added";
            billOfLadingHasErrors = true;
        }

        // Validate reason
        if (!reason || !reason.trim()) {
            reasonErrorMessage = "No reason selected";
            reasonHasErrors = true;
        }

        setFormState((prevState) => ({
            ...prevState,
            quantityHasErrors,
            quantityErrorMessage,
            transportHolderNumberHasErrors,
            transportHolderNumberErrorMessage,
            billOfLadingHasErrors,
            billOfLadingErrorMessage,
            reasonHasErrors,
            reasonErrorMessage,
            transportHolderNumber: transportHolderNumber.trim(),
            billOfLading: billOfLading.trim()
        }));

        if (quantityHasErrors || transportHolderNumberHasErrors || billOfLadingHasErrors || reasonHasErrors) {
            return;
        }

        inventoryService.getInventoryQuantityBySkuContainerBol(
            params.materialNumber as string,
            params.size as string,
            params.countryOfOrigin as string,
            params.quality as string,
            transportHolderNumber.trim(),
            billOfLading.trim()
        )
            .then((response) => {
                const availableQuantity = parseInt(response.data.getInventoryQuantityBySkuContainerBol, 10);

                if (quantity > availableQuantity) {
                    setErrorModalData({
                        transportHolderNumber: transportHolderNumber.trim(),
                        billOfLading: billOfLading.trim()
                    });
                    setErrorModalOpen(true);
                } else {
                    setDrsModalOpen(true); // Open DRS modal for confirmation
                }
            })
            .catch((error) => {
                setErrorModalData({
                    transportHolderNumber: transportHolderNumber.trim(),
                    billOfLading: billOfLading.trim()
                });
                setErrorModalOpen(true);
                console.error("Error fetching inventory quantity:", error);
            });
    };

    const closeErrorModal = () => {
        setErrorModalOpen(false);
        setErrorModalData(null);
    };

    const closeDrsModal = (isConfirmed) => {
        setDrsModalOpen(false);
        if (isConfirmed) {
            onClose();
        }
    };

    const formGroupStyle = {
        marginBottom: edsSpace16
    };

    const formGroupLastItemStyle = {
        marginBottom: edsSpace32
    };

    const radioGroupStyle = {
        width: "fit-content",
    };

    return (
        <div>
            <div className="form-group" style={formGroupStyle}>
                <RadioGroup label="Type"
                            name="typeRadioGroup"
                            valueSelected={formState.adjustmentType}
                            onChange={(e) => handleInputChange("adjustmentType", e.target.value as Type)}
                            className="eds-radio-group eds-grid eds-grid--m-cols-2"
                            style={radioGroupStyle}
                >
                    <Radio label="Remove" value={Type.NEGADJ} id="type_radio_1"/>
                </RadioGroup>
            </div>

            <div className="form-group" style={formGroupStyle}>
                <CheckboxGroup
                    label="Clear goods if needed"
                    name="checkbox-group"
                    onChange={e => handleInputChange("clearGoodsIfNeeded", e.target.checked)} id={""}                >
                    <Checkbox label="Yes" checked={formState.clearGoodsIfNeeded} id="clearGoodsIfNeeded_1" />
                </CheckboxGroup>
            </div>

            <div className="form-group" style={formGroupStyle}>
                <TextField id="quantity_text_field" label="Quantity" value={formState.quantity}
                           onChange={(e) => handleInputChange("quantity", e.target.value)}
                           hasErrors={formState.quantityHasErrors} errorMessage={formState.quantityErrorMessage}
                           onFocus={handleQuantityFocus}
                           onWheel={(e) => {
                               (e.target as HTMLElement).blur()
                           }} type="number"/>
            </div>

            <div className="form-group" style={formGroupStyle}>
                <TextField id="thn_text_field" label="TransportHolder Number" value={formState.transportHolderNumber}
                           onChange={(e) => handleInputChange("transportHolderNumber", e.target.value)}
                           hasErrors={formState.transportHolderNumberHasErrors}
                           errorMessage={formState.transportHolderNumberErrorMessage}
                           placeholder="Click to add a transportholder number"/>
            </div>

            <div className="form-group" style={formGroupStyle}>
                <TextField id="bol_text_field" label="Bill Of Lading" value={formState.billOfLading}
                           onChange={(e) => handleInputChange("billOfLading", e.target.value)}
                           hasErrors={formState.billOfLadingHasErrors}
                           errorMessage={formState.billOfLadingErrorMessage}
                           placeholder="Click to add a bill of lading"/>
            </div>

            <div className="form-group" style={formGroupStyle}>
                <Select
                    id="menu_text_field"
                    label="Reason"
                    value={formState.reason ? {value: formState.reason, label: formState.reason} : null}
                    onChange={(selectedOption: SingleValue<{ value: string, label: string }>) =>
                        handleInputChange("reason", selectedOption?.value)
                    }
                    options={reasonOptions}
                    placeholder="Click to select a reason code"
                    hasErrors={formState.reasonHasErrors}
                    errorMessage={formState.reasonErrorMessage}
                />
            </div>

            <div className="form-group" style={formGroupLastItemStyle}>
                <TextField id="comment_text_field" label="Comment" value={formState.comment}
                           onChange={(e) => handleInputChange("comment", e.target.value)}
                           placeholder="Click to add a comment"/>
            </div>

            <div className="form-group form-group-last-item" style={formGroupLastItemStyle}>
                <ButtonGroup>
                    <Button variant="primary" type="submit" onClick={validate}>Update Quantity</Button>
                    <Button variant="secondary" type="submit" onClick={() => onClose()}>Cancel</Button>
                </ButtonGroup>
            </div>

            {errorModalOpen && (
                <Modal
                    isOpen={errorModalOpen}
                    onDismiss={closeErrorModal}
                    headerSlot={
                        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                            <AlertCircle style={{ marginRight: 8, color: "#D7263D", width: "1.3em", height: "1.3em" }} />
                            <span>Inventory not found</span>
                        </span>
                    }
                    footerSlot={
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <ButtonGroup>
                                <Button variant="primary" onClick={closeErrorModal}>Close</Button>
                            </ButtonGroup>
                        </div>
                    }
                >
                    <div style={{ textAlign: "center" }}>
                        <Text>
                            No inventory found for:
                            <ul>
                                <li>TransportHolder Number = {errorModalData?.transportHolderNumber}</li>
                                <li>Bill Of Lading = {errorModalData?.billOfLading}</li>
                            </ul>
                            <br />
                            Please check the input values or contact support for assistance.
                        </Text>
                    </div>
                </Modal>
            )}

            {drsModalOpen && (
                <QuantityAdjustmentDrsModal
                    isOpen={drsModalOpen}
                    onClose={closeDrsModal}
                    formState={formState}
                    inventoryService={inventoryService}
                    host={host}
                    location={location}
                />
            )}
        </div>
    );
}

export {QuantityAdjustmentDrsForm};
